// Leute und Geräte (E1; seit E3, S7 neu nach Grundsatz 1 – PLAN.md,
// Nachtrag 19.10.: „jemanden einladen, ein Gerät freigeben oder sperren,
// jemanden austreten lassen – je ein Schritt, ohne Erklärung verständlich“).
// Eine Seite, von oben nach unten:
//   - Geräte, die auf eine Freigabe warten – ganz oben, nicht zu übersehen;
//   - „Jemanden einladen“ (der Knopf oben rechts): Name, E-Mail, Rolle als
//     Auswahl mit je einem Satz, was sie sieht – danach der Code, groß, zum
//     persönlichen Weitergeben;
//   - die Leute: Name, Rolle, E-Mail, Passkeys, Geräte, Stand – mit „Neuer
//     Code“, „Rolle ändern“ (nur die Leitung) und „Austreten lassen“ (=
//     sperren, mit Rückfrage; Löschen kommt später, O30);
//   - die Geräte: Freigeben und Sperren; das eigene ist erkennbar und nicht
//     sperrbar;
//   - „Wer sieht was“: je Rolle der Satz (leute/rollen.ts – eine Quelle).
// Alles wird alle 15 Sekunden neu geholt – ein neues Gerät erscheint von
// selbst. Was der Server abweist, steht an der Stelle, an der es geschah.
//
// Adressen: #/leute und #/geraete (dieselbe Seite, bei #/geraete gleich bei
// den Geräten).

import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  einladungscodeAnzeigen,
  neuerNutzerEingabe,
  ROLLEN_NAMEN,
  VERGEBBARE_ROLLEN,
  type EinladungAntwort,
  type GeraetZeile,
  type IchAntwort,
  type NutzerZeile,
  type Rolle,
} from "@happy-energy/kern";
import { Chip, Ikon, senden, Spaeter, toastZeigen } from "@happy-energy/oberflaeche";
import { Nachfrage } from "./bausteine.tsx";
import { GeraeteBereich, WartendeGeraete, type GeraetHandlungen } from "./Geraete.tsx";
import { Laedt, LadeFehler, useLaden } from "./laden.tsx";
import { initialen } from "./anzeige.ts";
import { geraeteJePerson, geraeteOrdnen, leuteOrdnen, personRechte, ROLLEN_IKON, ROLLEN_SAETZE, rollenZurWahl, woAnmelden } from "./leute/rollen.ts";
import { meldungAus } from "./meldung.ts";
import { Seite } from "./rahmen.tsx";
import { bisZeigen } from "./tage.ts";
import "./formular.css";
import "./leute/leute.css";

/** Was der Server sagt, wenn das Büro an einen Zugang der Leitung will. */
const NUR_LEITUNG = "Das darf nur die Leitung.";

// --- Die Rolle wählen (einladen, Rolle ändern) ----------------------------------------------------

function RollenWahl({ rollen, wert, aendern, jetzt }: { rollen: readonly Rolle[]; wert: Rolle | null; aendern: (r: Rolle) => void; jetzt?: Rolle }) {
  return (
    <div className="rollen-wahl" role="radiogroup" aria-label="Rolle">
      {rollen.map((r) => (
        <button key={r} type="button" role="radio" aria-checked={wert === r} className={`rolle-wahl${wert === r ? " an" : ""}`} onClick={() => aendern(r)}>
          <span className="avatar">
            <Ikon name={ROLLEN_IKON[r]} />
          </span>
          <span className="rolle-wahl-wort">
            <b>
              {ROLLEN_NAMEN[r]}
              {jetzt === r && <span className="leise"> · jetzt</span>}
            </b>
            <span>{ROLLEN_SAETZE[r]}</span>
          </span>
          <span className="rolle-wahl-punkt" aria-hidden="true">
            {wert === r && <Ikon name="haken" groesse="k" />}
          </span>
        </button>
      ))}
    </div>
  );
}

// --- Der Code, groß ------------------------------------------------------------------------------

function CodeFenster({ e, rolle, fertig }: { e: EinladungAntwort; rolle: Rolle; fertig: () => void }) {
  return (
    <div className="nachfrage-huelle crm-nachfrage">
      <div className="nachfrage-fenster code-fenster" role="dialog" aria-modal="true" aria-labelledby="code-titel">
        <span className="nachfrage-zeichen">
          <Ikon name="schloss" groesse="g" />
        </span>
        <h2 id="code-titel">Einladungscode für {e.name}</h2>
        <div className="gross-code">{einladungscodeAnzeigen(e.code)}</div>
        <div className="nachfrage-text">
          <p>
            <b>Persönlich weitergeben, nicht per Mail.</b> {e.name} tippt {woAnmelden(rolle)} auf „Ich habe einen Einladungscode“ und gibt ihn ein.
          </p>
          <p>Gilt bis {bisZeigen(e.gueltigBis)} und nur einmal. Ein älterer Code gilt damit nicht mehr.</p>
          <p className="klein-text">Der Code wird nur jetzt angezeigt. Geht er verloren, einfach einen neuen geben.</p>
        </div>
        <div className="crm-nachfrage-knoepfe">
          <button type="button" className="crm-knopf lime" onClick={fertig} autoFocus>
            <Ikon name="haken" groesse="k" /> Erledigt
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Jemanden einladen --------------------------------------------------------------------------

function Einladen({ ich, fertig, abbrechen }: { ich: IchAntwort; fertig: (e: EinladungAntwort, rolle: Rolle) => void; abbrechen: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rolle, setRolle] = useState<Rolle | null>(null);
  const [amFeld, setAmFeld] = useState<{ feld: string | null; meldung: string } | null>(null);
  const [sendet, setSendet] = useState(false);

  async function einladen(ev: FormEvent) {
    ev.preventDefault();
    if (rolle === null) {
      setAmFeld({ feld: "rolle", meldung: "Bitte eine Rolle wählen – sie bestimmt, was die Person sieht." });
      return;
    }
    const gelesen = neuerNutzerEingabe.safeParse({ name, email, rolle });
    if (!gelesen.success) {
      const erste = gelesen.error.issues[0];
      setAmFeld({ feld: typeof erste?.path[0] === "string" ? erste.path[0] : null, meldung: erste?.message ?? "Bitte die Felder prüfen." });
      return;
    }
    setAmFeld(null);
    setSendet(true);
    try {
      const e = await senden<EinladungAntwort>("/api/nutzer", gelesen.data);
      fertig(e, rolle);
    } catch (f) {
      setAmFeld({ feld: null, meldung: meldungAus(f, NUR_LEITUNG) });
    } finally {
      setSendet(false);
    }
  }

  return (
    <section className="crm-karte einladen-karte" aria-label="Jemanden einladen">
      <div className="karten-kopf">
        <Ikon name="plus" />
        <h2>Jemanden einladen</h2>
      </div>
      <form
        className="einladen"
        onSubmit={(e) => void einladen(e)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !sendet) abbrechen();
        }}
      >
        <p className="leise klein einladen-satz">Für jede Person ein eigener Zugang. Danach bekommst du einen Code, den du ihr persönlich gibst.</p>
        <div className="einladen-felder">
          <label className="crm-feld">
            Name
            <input
              className="crm-eingabe"
              value={name}
              autoFocus
              autoComplete="off"
              placeholder="Vorname Nachname"
              aria-invalid={amFeld?.feld === "name" ? true : undefined}
              onChange={(e) => setName(e.target.value)}
            />
            {amFeld?.feld === "name" && <span className="crm-fehler-satz">{amFeld.meldung}</span>}
          </label>
          <label className="crm-feld">
            E-Mail
            <input
              className="crm-eingabe"
              type="email"
              value={email}
              autoComplete="off"
              placeholder="name@firma.at"
              aria-invalid={amFeld?.feld === "email" ? true : undefined}
              onChange={(e) => setEmail(e.target.value)}
            />
            {amFeld?.feld === "email" && <span className="crm-fehler-satz">{amFeld.meldung}</span>}
          </label>
        </div>
        <div className="crm-feld-gruppe">
          <span className="crm-feld-titel">Rolle – was sie sieht</span>
          <RollenWahl rollen={rollenZurWahl(ich.nutzer.rolle)} wert={rolle} aendern={setRolle} />
          {amFeld?.feld === "rolle" && <span className="crm-fehler-satz">{amFeld.meldung}</span>}
        </div>
        {amFeld && amFeld.feld === null && (
          <p className="crm-fehler-satz" role="alert">
            {amFeld.meldung}
          </p>
        )}
        <div className="crm-knopf-reihe">
          <button type="submit" className="crm-knopf lime" disabled={sendet}>
            {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="schloss" groesse="k" />} Einladen und Code zeigen
          </button>
          <button type="button" className="crm-knopf rand" onClick={abbrechen} disabled={sendet}>
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}

// --- Eine Person ----------------------------------------------------------------------------------

function PasskeyChip({ n }: { n: NutzerZeile }) {
  if (!n.aktiv)
    return (
      <Chip art="fehler" ikon="schloss">
        ausgetreten
      </Chip>
    );
  if (n.passkeys > 0)
    return (
      <Chip art="ok" ikon="haken">
        {n.passkeys === 1 ? "1 Passkey" : `${n.passkeys} Passkeys`}
      </Chip>
    );
  if (n.offeneEinladung)
    return (
      <Chip art="warn" ikon="uhr">
        Code offen bis {bisZeigen(n.offeneEinladung.gueltigBis)}
      </Chip>
    );
  return (
    <Chip art="warn" ikon="achtung">
      noch nicht angemeldet – neuer Code nötig
    </Chip>
  );
}

function PersonKarte({
  n,
  ich,
  geraete,
  beschaeftigt,
  meldung,
  neuerCode,
  rolleAendern,
  austreten,
  wiederAufnehmen,
}: {
  n: NutzerZeile;
  ich: IchAntwort;
  geraete: readonly GeraetZeile[];
  beschaeftigt: string | null;
  meldung: string | undefined;
  neuerCode: () => void;
  rolleAendern: (r: Rolle) => void;
  austreten: () => void;
  wiederAufnehmen: () => void;
}) {
  const r = personRechte({ id: ich.nutzer.id, rolle: ich.nutzer.rolle }, n);
  const du = n.id === ich.nutzer.id;
  const [rolleOffen, setRolleOffen] = useState(false);
  const [neueRolle, setNeueRolle] = useState<Rolle>(n.rolle);
  const laeuft = beschaeftigt === n.id;

  return (
    <article className={`crm-karte person-karte${n.aktiv ? "" : " ausgetreten"}`}>
      <div className="person-kopf">
        <span className={`avatar${du ? " avatar-tuerkis" : n.aktiv ? "" : " avatar-aus"}`}>{initialen(n.name)}</span>
        <div className="person-wort">
          <b>
            {n.name}
            {du && <span className="leise"> · du</span>}
          </b>
          <span>{n.email}</span>
        </div>
        <Chip art="dunkel" ikon={ROLLEN_IKON[n.rolle]}>
          {ROLLEN_NAMEN[n.rolle]}
        </Chip>
      </div>
      <div className="chip-zeile">
        <PasskeyChip n={n} />
        {n.aktiv && n.passkeys > 0 && n.offeneEinladung && <Chip ikon="uhr">neuer Code offen bis {bisZeigen(n.offeneEinladung.gueltigBis)}</Chip>}
      </div>
      <div className="person-geraete">
        {geraete.length === 0 ? (
          <span className="leise klein">Noch kein Gerät angemeldet.</span>
        ) : (
          geraete.map((g) => (
            <span key={g.id} className={`person-geraet ${g.stand}`} title={`${g.name} · ${g.stand === "wartet" ? "wartet auf Freigabe" : g.stand}`}>
              <Ikon name={g.app === "doku" ? "handy" : "laptop"} groesse="k" /> {g.name}
              {g.stand === "wartet" && " · wartet"}
              {g.stand === "gesperrt" && " · gesperrt"}
            </span>
          ))
        )}
      </div>

      {rolleOffen && (
        <div className="rolle-aendern">
          <span className="crm-feld-titel">Neue Rolle für {n.name} – gilt sofort</span>
          <RollenWahl rollen={VERGEBBARE_ROLLEN} wert={neueRolle} aendern={setNeueRolle} jetzt={n.rolle} />
          <div className="crm-knopf-reihe">
            <button
              type="button"
              className="crm-knopf lime klein"
              disabled={neueRolle === n.rolle || beschaeftigt !== null}
              onClick={() => {
                rolleAendern(neueRolle);
                setRolleOffen(false);
              }}
            >
              <Ikon name="haken" groesse="k" /> Rolle ändern
            </button>
            <button type="button" className="crm-knopf rand klein" onClick={() => setRolleOffen(false)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {meldung && (
        <p className="crm-fehler-satz" role="alert">
          {meldung}
        </p>
      )}
      {r.grund && <p className="leise klein person-grund">{r.grund}</p>}

      {(r.neuerCode || r.rolleAendern || r.austreten || r.wiederAufnehmen) && !rolleOffen && (
        <div className="crm-knopf-reihe person-knoepfe">
          {r.neuerCode && (
            <button type="button" className="crm-knopf rand klein" onClick={neuerCode} disabled={beschaeftigt !== null}>
              {laeuft ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="schloss" groesse="k" />} Neuer Code
            </button>
          )}
          {r.rolleAendern && (
            <button
              type="button"
              className="crm-knopf rand klein"
              onClick={() => {
                setNeueRolle(n.rolle);
                setRolleOffen(true);
              }}
              disabled={beschaeftigt !== null}
            >
              <Ikon name="nutzer" groesse="k" /> Rolle ändern
            </button>
          )}
          {r.austreten && (
            <button type="button" className="crm-knopf leise klein person-austreten" onClick={austreten} disabled={beschaeftigt !== null}>
              Austreten lassen
            </button>
          )}
          {r.wiederAufnehmen && (
            <button type="button" className="crm-knopf rand klein" onClick={wiederAufnehmen} disabled={beschaeftigt !== null}>
              {laeuft ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="zurueck" groesse="k" />} Wieder aufnehmen
            </button>
          )}
        </div>
      )}
    </article>
  );
}

// --- Die Seite ------------------------------------------------------------------------------------

type Rueckfrage = { art: "austreten"; n: NutzerZeile } | { art: "sperren"; g: GeraetZeile };

export function Leute({ ich, bereich = "leute" }: { ich: IchAntwort; bereich?: "leute" | "geraete" }) {
  const leute = useLaden<NutzerZeile[]>("/api/nutzer");
  const geraete = useLaden<GeraetZeile[]>("/api/geraete");
  const neuLeute = leute.neu;
  const neuGeraete = geraete.neu;
  const [einladen, setEinladen] = useState(false);
  const [code, setCode] = useState<{ e: EinladungAntwort; rolle: Rolle } | null>(null);
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [meldungen, setMeldungen] = useState<Record<string, string>>({});

  // Ein neues Gerät, ein eingelöster Code – alle 15 Sekunden nachsehen.
  useEffect(() => {
    const uhr = setInterval(() => {
      neuLeute();
      neuGeraete();
    }, 15_000);
    return () => clearInterval(uhr);
  }, [neuLeute, neuGeraete]);

  // #/geraete: gleich zu den Geräten – außer es wartet eines, das steht ohnehin ganz oben.
  const geladen = leute.daten !== undefined && geraete.daten !== undefined;
  const esWartet = (geraete.daten ?? []).some((g) => g.stand === "wartet");
  // Nur einmal je Adresse – nicht bei jedem Nachsehen alle 15 Sekunden.
  const gesprungen = useRef<string | null>(null);
  useEffect(() => {
    if (!geladen || gesprungen.current === bereich) return;
    gesprungen.current = bereich;
    if (bereich === "geraete" && !esWartet) document.getElementById("geraete")?.scrollIntoView({ block: "start" });
  }, [bereich, geladen, esWartet]);

  /** Einen Schritt tun – danach alles neu holen; was schiefging, steht bei `ort` (Person oder Gerät). */
  async function tun(ort: string, arbeit: () => Promise<unknown>, gelungen: string | null): Promise<boolean> {
    setBeschaeftigt(ort);
    setMeldungen(({ [ort]: _alt, ...rest }) => rest);
    try {
      await arbeit();
      if (gelungen) toastZeigen(gelungen);
      return true;
    } catch (f) {
      setMeldungen((m) => ({ ...m, [ort]: meldungAus(f, NUR_LEITUNG) }));
      return false;
    } finally {
      setBeschaeftigt(null);
      neuLeute();
      neuGeraete();
    }
  }

  const personen = leute.daten ? leuteOrdnen(leute.daten, ich.nutzer.id) : [];
  const zuordnung = geraeteJePerson(geraete.daten ?? [], personen);
  const personVon = new Map(personen.map((n) => [n.id, n] as const));
  const wartend = geraeteOrdnen((geraete.daten ?? []).filter((g) => g.stand === "wartet"));
  const uebrige = geraeteOrdnen((geraete.daten ?? []).filter((g) => g.stand !== "wartet"));

  const h: GeraetHandlungen = {
    darf: (g) => {
      const besitzer = personVon.get(g.nutzer.id);
      return besitzer ? personRechte({ id: ich.nutzer.id, rolle: ich.nutzer.rolle }, besitzer).geraete : true;
    },
    freigeben: (g) => void tun(g.id, () => senden(`/api/geraete/${g.id}/freigeben`), `„${g.name}“ freigegeben`),
    sperren: (g) => setRueckfrage({ art: "sperren", g }),
    beschaeftigt,
    meldung: (g) => meldungen[g.id],
  };

  const fehler = leute.fehler ?? geraete.fehler;

  return (
    <Seite
      titel="Leute und Geräte"
      unter={`Wer bei ${ich.firma.name} mit dem System arbeitet – und auf welchen Geräten`}
      knoepfe={
        !einladen && leute.daten ? (
          <button type="button" className="crm-knopf lime klein" onClick={() => setEinladen(true)}>
            <Ikon name="plus" groesse="k" /> Jemanden einladen
          </button>
        ) : (
          <span />
        )
      }
    >
      {fehler !== undefined && (
        <LadeFehler
          fehler={fehler}
          nochmal={() => {
            neuLeute();
            neuGeraete();
          }}
        />
      )}
      {!geladen && fehler === undefined && <Laedt />}
      {geladen && (
        <>
          <WartendeGeraete geraete={wartend} ichGeraetId={ich.geraet.id} h={h} />

          {einladen && (
            <Einladen
              ich={ich}
              abbrechen={() => setEinladen(false)}
              fertig={(e, rolle) => {
                setEinladen(false);
                setCode({ e, rolle });
                neuLeute();
              }}
            />
          )}

          <div className="crm-abschnitt">
            <h2>Die Leute</h2>
            <span className="leise klein">
              {personen.filter((n) => n.aktiv).length} aktiv
              {personen.some((n) => !n.aktiv) && ` · ${personen.filter((n) => !n.aktiv).length} ausgetreten`}
            </span>
          </div>
          <div className="personen-raster">
            {personen.map((n) => (
              <PersonKarte
                key={n.id}
                n={n}
                ich={ich}
                geraete={zuordnung.jePerson.get(n.id) ?? []}
                beschaeftigt={beschaeftigt}
                meldung={meldungen[n.id]}
                neuerCode={() =>
                  void tun(
                    n.id,
                    async () => setCode({ e: await senden<EinladungAntwort>(`/api/nutzer/${n.id}/einladung`), rolle: n.rolle }),
                    null,
                  )
                }
                rolleAendern={(rolle) => void tun(n.id, () => senden(`/api/nutzer/${n.id}/rolle`, { rolle }), `${n.name} ist jetzt ${ROLLEN_NAMEN[rolle]} – gilt sofort`)}
                austreten={() => setRueckfrage({ art: "austreten", n })}
                wiederAufnehmen={() => void tun(n.id, () => senden(`/api/nutzer/${n.id}/entsperren`), `${n.name} ist wieder aufgenommen`)}
              />
            ))}
          </div>

          <GeraeteBereich geraete={uebrige} ichGeraetId={ich.geraet.id} h={h} />

          <section className="crm-karte wer-sieht-was" aria-label="Wer sieht was">
            <div className="karten-kopf">
              <Ikon name="auge" />
              <h2>Wer sieht was</h2>
              <span className="leise klein">Jede Rolle sieht ihre Sache, die Leitung alles.</span>
            </div>
            <ul className="wer-liste">
              {VERGEBBARE_ROLLEN.map((r) => (
                <li key={r}>
                  <span className="avatar">
                    <Ikon name={ROLLEN_IKON[r]} groesse="k" />
                  </span>
                  <span>
                    <b>{ROLLEN_NAMEN[r]}</b> {ROLLEN_SAETZE[r]}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <Spaeter titel="Zugang und Geräte ganz löschen" wann="später · Datenschutz">
            „Austreten lassen“ sperrt sofort. Ganz löschen – samt Geräten und Passkeys – kommt mit den Regeln zum Datenschutz (Verarbeitungsverzeichnis,
            Löschfristen).
          </Spaeter>
        </>
      )}

      {rueckfrage?.art === "austreten" && (
        <Nachfrage
          titel={`${rueckfrage.n.name} austreten lassen?`}
          jaText="Austreten lassen"
          gefahr
          ikon="schloss"
          beschaeftigt={beschaeftigt !== null}
          nein={() => setRueckfrage(null)}
          ja={() => {
            const n = rueckfrage.n;
            void tun(n.id, () => senden(`/api/nutzer/${n.id}/sperren`), `${n.name} ist ausgetreten`).then(() => setRueckfrage(null));
          }}
        >
          <p>
            {rueckfrage.n.name} kommt sofort von allen Geräten nicht mehr hinein. Löschen kommt später (Datenschutz).
          </p>
          <p>„Wieder aufnehmen“ geht jederzeit.</p>
        </Nachfrage>
      )}
      {rueckfrage?.art === "sperren" && (
        <Nachfrage
          titel={`„${rueckfrage.g.name}“ sperren?`}
          jaText="Sperren"
          gefahr
          ikon="schloss"
          beschaeftigt={beschaeftigt !== null}
          nein={() => setRueckfrage(null)}
          ja={() => {
            const g = rueckfrage.g;
            void tun(g.id, () => senden(`/api/geraete/${g.id}/sperren`), `„${g.name}“ gesperrt`).then(() => setRueckfrage(null));
          }}
        >
          <p>
            {rueckfrage.g.nutzer.name} kommt mit diesem Gerät ({rueckfrage.g.app === "doku" ? "Doku-App" : "CRM"}) sofort nicht mehr hinein. Freigeben geht jederzeit
            wieder.
          </p>
        </Nachfrage>
      )}
      {code && <CodeFenster e={code.e} rolle={code.rolle} fertig={() => setCode(null)} />}
    </Seite>
  );
}
